import { List, Map } from 'immutable';
import { omitBy } from 'lodash';
import { EntityTypesEnum } from '../../../app.enum';
import { hasValue, IChangeField } from '../../../utils/helpers/model.helper';
import { Document } from '../../document-data/document.model';
import { DocumentPreview } from '../../document-data/sub-models/document-preview.model';
import {
  entityKeys,
  entityModelFactory,
  generateContent,
} from '../../entity-data/entity.model';
import { IModelValidator } from '../../shared/interfaces/model-validator.interface';
import { BilledDocument } from '../../tracked-time-data/models/billed-document.model';
import { ClientPreview } from '../../tracked-time-data/models/client-preview.model';
import { ClientReference } from '../../tracked-time-data/models/client-reference.model';
import { ITimeEntry } from '../appointment-data.data';
import { AppointmentContact } from './appointment-contact.model';
import { LinkedDocumentReference } from './linked-document-reference.model';
import { Reminders } from './reminders.model';

export const PARENT_SCHEMA = Object.freeze({
  METADATA: null,
  CLIENT_PREVIEW: null, // ClientPreview
  BILLED_DOCUMENT: null, // BilledDocument
  BILLED_DOCUMENT_PREVIEW: null, // DocumentPreview
  LINKED_DOCUMENT_SNIPPETS: List<Document>(),
});
export const CONTENT_SCHEMA = Object.freeze({
  CLIENT: null, // ClientReference
  CONTACT: AppointmentContact,
  START_DATE: null, // i2g-date-time (yyyy-mm-dd'T'hh:mm:ssZ)
  DURATION: 0, // seconds
  NOTES: '',
  LINKED_DOCUMENTS: List<LinkedDocumentReference>(),
  REMINDERS: Reminders,
  LOCALE: '',
});
const KEYS = entityKeys(PARENT_SCHEMA, CONTENT_SCHEMA);
export const CONTENT_KEYS = generateContent(CONTENT_SCHEMA).KEYS;
const parentChanges = (props: Map<string, any>): IChangeField[] => {
  const clientPreview = props.get(KEYS.CLIENT_PREVIEW);
  const billedDocument = props.get(KEYS.BILLED_DOCUMENT);
  const billedDocumentPreview = props.get(KEYS.BILLED_DOCUMENT_PREVIEW);
  const linkedDocumentSnippets = props.get(KEYS.LINKED_DOCUMENT_SNIPPETS);

  return [
    {
      key: KEYS.CLIENT_PREVIEW,
      value: clientPreview ? new ClientPreview(clientPreview) : null,
    },
    {
      key: KEYS.BILLED_DOCUMENT,
      value: billedDocument ? new BilledDocument(billedDocument) : null,
    },
    {
      key: KEYS.BILLED_DOCUMENT_PREVIEW,
      value: billedDocumentPreview
        ? new DocumentPreview(billedDocumentPreview)
        : null,
    },
    {
      key: KEYS.LINKED_DOCUMENT_SNIPPETS,
      value: linkedDocumentSnippets
        ? linkedDocumentSnippets.map((params) => new Document(params))
        : List<Document>([]),
    },
  ];
};
const contentChanges = (contentProps: Map<string, any>): IChangeField[] => {
  const client = contentProps.get(CONTENT_KEYS.CLIENT);
  const contact = contentProps.get(CONTENT_KEYS.CONTACT) || {};
  const linkedDocuments = contentProps.get(CONTENT_KEYS.LINKED_DOCUMENTS);

  return [
    {
      key: CONTENT_KEYS.CLIENT,
      value: client ? new ClientReference(client) : null,
    },
    {
      key: CONTENT_KEYS.CONTACT,
      value: new AppointmentContact(contact),
    },
    {
      key: CONTENT_KEYS.LINKED_DOCUMENTS,
      value: linkedDocuments
        ? linkedDocuments.map((params) => new LinkedDocumentReference(params))
        : List<LinkedDocumentReference>([]),
    },
  ];
};

export class Appointment
  extends entityModelFactory(
    PARENT_SCHEMA,
    parentChanges,
    CONTENT_SCHEMA,
    contentChanges
  )
  implements IModelValidator, ITimeEntry {
  static ENTITY_TYPE: EntityTypesEnum = EntityTypesEnum.appointment;
  static KEYS: any = KEYS;
  static CONTENT_KEYS: any = CONTENT_KEYS;

  get ENTITY_TYPE(): string {
    return Appointment.ENTITY_TYPE;
  }

  get billingName(): string {
    return this.clientPreview ? this.clientPreview.billingName : null;
  }

  get clientPreview(): ClientPreview {
    return this.get(KEYS.CLIENT_PREVIEW);
  }

  get billedDocument(): BilledDocument {
    return this.get(KEYS.BILLED_DOCUMENT);
  }

  get billedDocumentPreview(): DocumentPreview {
    return this.get(KEYS.BILLED_DOCUMENT_PREVIEW);
  }

  get linkedDocumentSnippets(): List<Document> {
    return this.get(KEYS.LINKED_DOCUMENT_SNIPPETS);
  }

  get clientReference(): ClientReference {
    return this.getFieldInContent(CONTENT_KEYS.CLIENT);
  }

  get contact(): AppointmentContact {
    return this.getFieldInContent(CONTENT_KEYS.CONTACT);
  }

  get startDate(): string {
    return this.getFieldInContent(CONTENT_KEYS.START_DATE);
  }

  get duration(): number {
    return this.getFieldInContent(CONTENT_KEYS.DURATION);
  }

  get notes(): string {
    return this.getFieldInContent(CONTENT_KEYS.NOTES);
  }

  get linkedDocumentReferences(): List<LinkedDocumentReference> {
    return this.getFieldInContent(CONTENT_KEYS.LINKED_DOCUMENTS);
  }

  get locale(): string {
    return this.getFieldInContent(CONTENT_KEYS.LOCALE);
  }

  hasValidClient(): boolean {
    return this.clientPreview && !this.clientPreview.isDeleted();
  }

  hasValue(): boolean {
    const hasFieldsPopulated = hasValue(
      KEYS,
      omitBy(
        CONTENT_SCHEMA,
        (_, key) => key.toLowerCase() === CONTENT_KEYS.CLIENT.toLowerCase()
      ),
      this.content.toMap()
    );

    return (
      hasFieldsPopulated ||
      (!!this.clientReference && this.clientReference.hasValue())
    );
  }
}
